import { RouteConfig, Route } from 'vue-router';
import { defineComponent } from 'vue';
import { IClients } from '@llws/lift-entity-interfaces';
import { routeGroups, routes } from '@/router';
import { mapModuleGetters, mapModuleActions } from '@/shared/vuex-toolkit';
import { main, users } from '@/store/typed/typed-modules';
import { IRouteMetaFooterBar } from '@/shared';
import { IRouteGroup } from '@/shared/interfaces/route-group';
import { sidebarHeaderConfigs } from '@/shared/helpers/sidebar-header-configs';
import { getClientLogoURL } from '@/shared/helpers/get-client-logo-url';
import { getRouteMetaTitle } from '@/shared/helpers/route-meta-translations';

/**
 * Used in a watcher to set a session storage key containing the full path of a current route before being navigated from.
 * Solves a UX issue where the App's "Back" button did not preserve query string state when using it to navigate backwards,
 * which in effect wasn't preserving filters that might have been applied to list items.
 *
 * Example: Preserving sorting on units page
 * after navigating to a single page and then clicking the "Back" button on the app (NOT BROWSER BACK BUTTON)
 *
 * Used in `RsLayoutApp.vue` on `$route` watcher. Then referenced by the `backButtonConfig` computed property.
 * @param to `Route` object navigating to
 * @param from `Route` object navigating from (current $route);
 */
const setSessionPathOnRouteChange = (
  to: Route,
  from?: Route,
) => {
  if (from && to.name !== from.name && window.sessionStorage) {
    sessionStorage.setItem(from.name!, from.fullPath);
  }
};

export default defineComponent({
  data() {
    return {
      childRouteMaxCount: 12,
      routeGroups,
      routes,
      expandedPath: '' as string,
      formattedRouteNames: {} as Record<string, string>,
    };
  },

  computed: {
    ...mapModuleGetters(main, { headerParams: 'sidebarHeaderData' }),
    ...mapModuleGetters(users, { clients: 'accessibleClients' }),

    filteredRouteGroups(): IRouteGroup[] {
      return this.routeGroups.filter((g) => (
        (g.env.length ? g.env.includes(this.$env?.VUE_APP_ENV) : true)
        && (g.permissions.length ? this.$rsGlobals.$currentUser.hasPermission(g.permissions) : true)
        && !g.isHidden()
      ));
    },

    backButtonConfig(): { handler(): void, title: string } | null {
      const route = this.$route;
      const group = this.getRouteGroup(route);

      if (route?.meta?.backButtonTarget) {
        const backButtonTarget = route.meta.backButtonTarget();
        if (!backButtonTarget) return null;

        if (backButtonTarget?.isCustom) {
          return {
            title: 'N/A',
            handler: backButtonTarget.handler,
          };
        }

        return {
          title: getRouteMetaTitle(this, backButtonTarget.target!),
          handler: () => this.$router.push(
            sessionStorage?.getItem(backButtonTarget!.target.name!)
            || { name: backButtonTarget!.target.name },
          ),
        };
      }

      if (group?.routes.some((r) => r?.meta?.parent && r.name === route.name)) {
        if (route?.meta?.parent) {
          const backRoute = route.meta.parent.redirect ? route.meta.parent?.meta?.parent : route.meta.parent;
          return backRoute ? {
            title: getRouteMetaTitle(this, backRoute),
            handler: () => this.$router.push(sessionStorage?.getItem(backRoute.name!) || { name: backRoute.name }),
          } : null;
        }

        return {
          handler: () => this.$router.go(-1),
          title: 'Back',
        };
      }

      // No group, look for two levels of parent.
      if (route?.meta?.parent?.meta?.parent) {
        return {
          handler: () => this.$router.push(
            sessionStorage?.getItem(route?.meta?.parent?.meta?.parent?.name ?? '')
            || { name: route?.meta?.parent?.meta?.parent?.name },
          ),
          title: getRouteMetaTitle(this, route?.meta?.parent?.meta?.parent),
        };
      }

      return null;
    },

    clientLogoUrl(): string {
      return getClientLogoURL(this.$rsGlobals.$currentClient);
    },
  },

  watch: {
    $route: {
      immediate: true,
      deep: true,
      handler(newRoute: Route, oldRoute: Route) {
        if (this.$refs.avatarDropdown) {
          // Not sure how to ditch any here when calling a ref method.
          // It's aggravating.
          (this.$refs.avatarDropdown as any).setInvisible();
        }
        this.expandedPath = this.$route.path;

        this.$rsGlobals.$setFooterBottomText('');
        this.$rsGlobals.$setFooterLastUpdated(null);
        setSessionPathOnRouteChange(newRoute, oldRoute);
      },
    },
  },

  methods: {
    ...mapModuleActions(users, {
      actionGetClients: 'getAccessibleClients',
      actionChangeClient: 'changeCurrentClient',
    }),

    getRouteGroup(route: Route): IRouteGroup | null {
      return routeGroups.find((g) => g.routes
        .some((r) => r.name === route.name || r.children?.some((cr) => cr.name === route.name)))!;
    },

    getGroupChildren(group: IRouteGroup): RouteConfig[] {
      return group.routes.filter((r) => (
        !r?.meta?.parent
        && (r?.meta?.dynamicHideFromNav ? !r.meta.dynamicHideFromNav() : !r?.meta?.hideFromNav)
        && this.isRoutePermitted(r as Route)
      ));
    },

    getFormattedRoute(routeName: string): string {
      return this.formattedRouteNames[routeName.replace(' ', '_').toLowerCase()] ? this.formattedRouteNames[routeName.replace(' ', '_').toLowerCase()] : routeName;
    },

    getRouteTree(route: RouteConfig): RouteConfig[] {
      let tree = [route];
      let curRoute = route;

      while (curRoute?.meta?.parent) {
        curRoute = curRoute.meta.parent;
        tree = [...tree, curRoute];
      }

      return tree.reverse();
    },

    handleSidebarDataInRouteTree(route: Route): RouteConfig | null {
      const config = sidebarHeaderConfigs.find((v) => (route.matched || []).some((m) => m.path.startsWith(v.routeStartsWith)));

      if (
        this.headerParams
        && Object.keys(this.headerParams).length
        && config
        && ('redirectRoute' in this.headerParams)
      ) {
        const redirect = this.headerParams.redirectRoute || {
          path: '',
          name: 'CustomHeader',
          meta: {
            auth: { required: true },
            title: this.headerParams.title,
          },
        };

        if (redirect.meta) {
          redirect.meta.title = this.headerParams.title;
        }
        return redirect;
      }

      return null;
    },

    getChildRouterParent(route: Route) {
      return this.getRouteGroup(route)?.routes.find((r) => (
        (r.children || []).some((cr) => cr.name === route.name)
      ));
    },

    getAdjacentChildRoutes(route: Route): Array<RouteConfig> {
      const routeToCheck = this.getChildRouterParent(route) || route;
      const group = this.getRouteGroup(routeToCheck as Route);

      return group
        ? group.routes.filter((r) => (
          routeToCheck?.meta?.parent
          && r?.meta?.parent?.name === routeToCheck.meta.parent?.name
          && !r?.meta?.hideFromNav
          && (r?.meta?.dynamicHideFromNav ? !r.meta.dynamicHideFromNav() : !r?.meta?.hideFromNav)
          && this.isRoutePermitted(r as Route)
        ))
        : this.routes.filter((r) => (
          (
            route?.meta?.parent
              ? r?.meta?.parent?.name === route.meta.parent.name
              : r?.meta?.parent?.name === route.name
          )
          && (r?.meta?.dynamicHideFromNav ? !r.meta.dynamicHideFromNav() : !r?.meta?.hideFromNav)
          && this.isRoutePermitted(r as Route)
        ));
    },

    getFooterSettings(route: RouteConfig): IRouteMetaFooterBar {
      return route?.meta?.footerBar ?? {
        enabled: false,
        hasSave: false,
        hasTranslations: false,
        hasBottomText: false,
        hasLastUpdated: false,
        hasCustomText: false,
        hasCheckBox: false,
      };
    },

    getClientLogoURL(client: IClients) {
      let logoUrl = '/img/default-user.png';
      const prefix = this.getFileLoc(this.$rsGlobals.$s3Url!, client.clientKey, 'images/logos', '');
      if (client.primaryLogo) {
        logoUrl = `${prefix}${client.primaryLogo}`;
      }
      return logoUrl;
    },

    isRouteGroupActive(group: IRouteGroup): boolean {
      if (!this.$route) {
        return false;
      }

      return this.getRouteGroup(this.$route)?.name === group.name;
    },

    isGroupChildRouteActive(route: RouteConfig): boolean {
      const formattedRoute = this.$route;
      let curRoute = this.getChildRouterParent(formattedRoute) || formattedRoute;

      if (curRoute.name === route.name) {
        return true;
      }

      while (curRoute?.meta?.parent) curRoute = curRoute.meta.parent;

      return curRoute.name === route.name;
    },

    isCurrentChildRouteBottomChild(route: Route): boolean {
      return !!(
        route?.meta?.parent
        && !this.getRouteGroup(route)?.routes.some((r) => r.meta?.parent?.name === route.name)
      );
    },

    isPrimaryRouteInSubNavActive(route: RouteConfig): boolean {
      return (this.$route.meta!.parent === route && this.$route.meta!.hideFromNav)
        || (this.$route.meta!.parent?.meta?.parent === route)
        || (this.$route.path === route.path);
    },

    isRoutePermitted(route: Route): boolean {
      if (route?.meta?.auth) {
        return route.meta.auth.isDynamicPermitted
          ? route.meta.auth.isDynamicPermitted()
          : this.$rsGlobals.$currentUser.hasPermission(route.meta.auth.permissions || []);
      }

      return true;
    },

    handleSaveClicked(): void {
      if (this.$rsGlobals.globalSaveBtnConfig.handler) {
        this.$rsGlobals.globalSaveBtnConfig.handler();
      } else {
        console.warn('GLOBAL SAVE BUTTON CLICKED. Register an event handler in the global this.globalSaveBtnConfig object');
      }
    },

    handleDeleteClicked(): void {
      if (this.$rsGlobals.globalDeleteBtnConfig.handler) {
        this.$rsGlobals.globalDeleteBtnConfig.handler();
      } else {
        console.warn('GLOBAL DELETE BUTTON CLICKED. Register an event handler in the global this.globalDeleteBtnConfig object');
      }
    },

    handleCheckBoxClicked(): void {
      if (this.$rsGlobals.globalCheckBoxConfig.handler) {
        this.$rsGlobals.globalCheckBoxConfig.handler();
      } else {
        console.warn('GLOBAL CHECKBOX CLICKED. Register an event handler in the global this.globalCheckBoxConfig object');
      }
    },

    handleTranslationBtnClicked(): void {
      if (this.$rsGlobals.globalTranslationsBtnConfig.handler) {
        this.$rsGlobals.globalTranslationsBtnConfig.handler();
      } else {
        console.warn('GLOBAL TRANSLATIONS BUTTON CLICKED. Register an event handler in the global this.globalCheckBoxConfig object');
      }
    },

    async handleClientSelect(data: IClients): Promise<void> {
      await this.actionChangeClient(data.id!);
      window.location.replace(Object.keys(this.$route.params || {}).length > 0 ? '/' : this.$route.path);
    },
  },
});
